import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import { DotLoader } from "../../../components/loader/Loader";
import { format } from "date-fns";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Lock,
  XCircle,
  Upload,
  Trash2,
  ShieldAlert,
  Sparkles,
  Check,
  ClipboardList,
  MapPin,
  User,
  FileText,
  Calendar,
  Camera,
  CheckSquare,
} from "lucide-react";
import { useS3Upload, getS3SignedUrl } from "../../../utils/s3Client";
import { useOrgUsersStore, useRoleManageStore } from "../../../store/store";
import { useAuth } from "../../../auth/AuthProvider";
import { rolesObjType } from "../../../types/api";
interface WashroomStatus {
  _id: string;
  currentStatus: string;
  lastCleanedAt?: string;
  issues?: Array<{
    _id?: string;
    reportedAt: string;
    reportedBy: string;
    notes: string;
    resolved: boolean;
    resolvedAt?: string;
    resolvedBy?: string;
  }>;
  needsCleaning: boolean;
  evidenceFiles?: string[];
  cleaningDuration?: number;
  assignedStaff?: string;
  washroomName?: string;
  floor?: string;
  premisesId?: string;
  regionId?: string;
}

interface IncidentCreateData {
  department: string;
  incidentDate: string;
  reportDate: string;
  reportedBy: string;
  description: string;
  severity: "Low" | "Medium" | "High" | "Critical";
  status: "Open";
  washroomId?: string;
  premisesId?: string;
  regionId?: string;
}

const checklistItems = [
  "Toilets cleaned and disinfected",
  "Sinks and mirrors cleaned",
  "Floors mopped",
  "Trash emptied",
  "Supplies restocked",
  "No odors present",
];

const statusOptions = [
  {
    value: "Clean",
    label: "Clean",
    icon: CheckCircle2,
    activeBorder: "border-green-500",
    activeBg: "bg-green-50/50",
    activeText: "text-green-700",
    iconActiveBg: "bg-green-100",
  },
  {
    value: "In-Progress",
    label: "In Progress",
    icon: Clock,
    activeBorder: "border-yellow-500",
    activeBg: "bg-yellow-50/50",
    activeText: "text-yellow-700",
    iconActiveBg: "bg-yellow-100",
  },
  {
    value: "Needs Attention",
    label: "Needs Attention",
    icon: AlertTriangle,
    activeBorder: "border-red-500",
    activeBg: "bg-red-50/50",
    activeText: "text-red-700",
    iconActiveBg: "bg-red-100",
  },
  {
    value: "Closed",
    label: "Closed",
    icon: Lock,
    activeBorder: "border-slate-500",
    activeBg: "bg-slate-50/50",
    activeText: "text-slate-700",
    iconActiveBg: "bg-slate-100",
  },
  {
    value: "Not Cleaned",
    label: "Not Cleaned",
    icon: XCircle,
    activeBorder: "border-orange-500",
    activeBg: "bg-orange-50/50",
    activeText: "text-orange-700",
    iconActiveBg: "bg-orange-100",
  },
];

const WashroomStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, orgUser, orgAdmin, roleData } = useAuth();
  const [status, setStatus] = useState<WashroomStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(
    location.state?.initialStatus || "",
  );
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklist, setChecklist] = useState<Record<string, boolean>>(
    checklistItems.reduce((acc, item) => ({ ...acc, [item]: false }), {}),
  );

  // File upload states
  const [evidenceFiles, setEvidenceFiles] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>("");
  const [signedImgUrls, setSignedImgUrls] = useState<string[]>([]);

  // Time tracking states
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>("00:00:00");

  // Staff reassignment for admin
  const [selectedNewStaff, setSelectedNewStaff] = useState("");

  // Use the S3 upload hook (goes through the backend's presigned-URL
  // endpoints - never talks to AWS with credentials in the browser)
  const { uploadFileToS3 } = useS3Upload();

  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  const {
    rolesData,
    fetchAllRoles,
  }: { rolesData: rolesObjType[]; fetchAllRoles: () => void } =
    useRoleManageStore();

  // Define role-based permissions.
  // Workflow tier comes from the role's `staffCategory`, not from CRUD
  // permission flags - those only gate raw API/UI access, not job function.
  const isOrgAdminAccount =
    user?.roles.includes("orgadmin") || user?.roles.includes("superadmin");
  const staffCategory = roleData?.staffCategory;
  const isAdmin = isOrgAdminAccount || staffCategory === "admin";
  const isWashroomMaintenance = !isAdmin && staffCategory === "maintenance";
  const isPerformWashroomTask = !isAdmin && !isWashroomMaintenance;

  // For washroom-maintenance role, they should see all sections and be able to update status
  const canUpdateStatus = isWashroomMaintenance || !isAdmin;
  const canReassignStaff = isAdmin;
  const canResolveIssues = isAdmin || isWashroomMaintenance;

  // Special view mode for admin when dealing with "Needs Attention" status
  const isAdminNeedsAttentionView =
    isAdmin && status?.currentStatus === "Needs Attention";

  // Function to create auto incident
  const createAutoIncident = async (washroomData: any, notes: string) => {
    try {
      const currentUser = (orgUser || user) as any;
      const reportedByName = (
        currentUser?.firstName
          ? `${currentUser.firstName} ${currentUser.lastName || ""}`.trim()
          : orgAdmin?.org_admin_name || currentUser?.name || currentUser?.username || "System"
      );

      const incidentData: IncidentCreateData = {
        department: "Washroom Management",
        incidentDate: new Date().toISOString(),

        reportDate: new Date().toISOString(),
        reportedBy: reportedByName,
        description: `Washroom Maintenance Issue: ${
          washroomData.washroomName || "Unknown Washroom"
        }\n\nIssue Details: ${notes}\n\nLocation: ${
          washroomData.floor || "Unknown Floor"
        }\nWashroom ID: ${washroomData._id}`,
        severity: "Medium",
        status: "Open",
        washroomId: washroomData._id,
        premisesId: washroomData.premisesId,
        regionId: washroomData.regionId,
      };

      const response = await axiosInstance.post(
        `/api/incidents`,
        incidentData,
      );

      if (response.data) {
        toast.success("Incident ticket created automatically");
        return response.data;
      }
    } catch (error) {
      console.error("Failed to create auto incident:", error);
      toast.error("Failed to create automatic incident ticket");
      throw error;
    }
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axiosInstance.get(`/api/washroom/${id}`);
        const washroomData = response.data.message;
        setStatus(washroomData);

        if (washroomData?.evidenceFiles) {
          setEvidenceFiles(washroomData.evidenceFiles);
        }

        if (washroomData?.cleaningStartTime && !washroomData.cleaningEndTime) {
          setStartTime(new Date(washroomData.cleaningStartTime));
        }

        if (!location.state?.initialStatus) {
          setSelectedStatus(washroomData?.currentStatus || "");
        }
      } catch (error) {
        toast.error("Failed to fetch washroom status");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [id, location.state?.initialStatus]);

  // Timer effect for In-Progress status
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (selectedStatus === "In-Progress" && startTime) {
      interval = setInterval(() => {
        const currentTime = new Date();
        const diff = currentTime.getTime() - startTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setElapsedTime(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
        );
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [selectedStatus, startTime]);

  // Fetch signed URLs for existing evidence files
  useEffect(() => {
    if (!evidenceFiles.length) {
      setSignedImgUrls([]);
      return;
    }

    const fetchSignedUrls = async () => {
      try {
        const urls: string[] = await Promise.all(
          evidenceFiles.map(async (fileKey) => {
            try {
              const { fileName, fileType } = parseFileKey(fileKey);
              const signedUrl = await getS3SignedUrl(fileName, fileType);
              return signedUrl;
            } catch (error) {
              console.error(`Failed to get signed URL for ${fileKey}:`, error);
              return "";
            }
          }),
        );
        setSignedImgUrls(urls.filter((url) => url !== ""));
      } catch (error) {
        console.error("Failed to fetch signed URLs:", error);
        setSignedImgUrls([]);
      }
    };

    fetchSignedUrls();
  }, [evidenceFiles]);

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([fetchOrgUsers(), fetchAllRoles()]);
    };
    initializeData();
  }, [fetchOrgUsers, fetchAllRoles]);

  const getUserNameById = (userId: string) => {
    const user = orgUsersData.find((u: any) => u._id === userId);
    if (!user) return "Unknown User";
    return `${user.firstName} ${user.lastName}`;
  };

  const parseFileKey = (
    fileString: string,
  ): { fileName: string; fileType: string } => {
    const [fileName, fileType] = fileString.split("|");
    return { fileName, fileType };
  };

  const getWashroomMaintenanceStaff = () => {
    if (!rolesData.length || !orgUsersData.length) return [];

    // Any role tagged as "maintenance" counts - not one hardcoded role name.
    // This lets an org define multiple maintenance-tier roles (e.g.
    // "Plumbing Technician", "Electrician") that all show up here.
    const maintenanceRoleIds = new Set(
      rolesData
        .filter((role: rolesObjType) => role.staffCategory === "maintenance")
        .map((role: rolesObjType) => role._id),
    );

    if (!maintenanceRoleIds.size) {
      return [];
    }

    const maintenanceStaff = orgUsersData.filter((user: any) =>
      maintenanceRoleIds.has(user.role),
    );

    return maintenanceStaff;
  };

  // Fixed: Handle multiple files upload in batch
  const handleMultipleFilesUpload = async (files: FileList) => {
    try {
      setUploading(true);
      setUploadErrorMessage("");

      const maxSize = 5 * 1024 * 1000; // 5MB in bytes
      const validFiles: File[] = [];
      let hasInvalidFile = false;

      Array.from(files).forEach((file) => {
        if (file.size > maxSize) {
          hasInvalidFile = true;
        } else {
          validFiles.push(file);
        }
      });

      if (hasInvalidFile) {
        setUploadErrorMessage("Some files exceed the 5MB size limit.");
        setUploading(false);
        return;
      }

      if (validFiles.length === 0) {
        setUploading(false);
        return;
      }

      const uploadPromises = validFiles.map(async (file) => {
        try {
          // uploadFileToS3 PUTs the file straight to S3 via a short-lived
          // presigned URL from the backend and resolves with the object's
          // real (namespaced, unique) S3 key - not a filename.
          const key = await uploadFileToS3(file, {
            premiseId: status?.premisesId,
          });
          if (key) {
            const fileKey = `${key}|${file.type}`;
            const signedUrl = await getS3SignedUrl(key, file.type);
            return { fileKey, signedUrl };
          }
          return null;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          return null;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(
        (result): result is { fileKey: string; signedUrl: string } =>
          result !== null,
      );

      if (successfulUploads.length > 0) {
        const newFileKeys = successfulUploads.map((result) => result.fileKey);
        const newSignedUrls = successfulUploads.map(
          (result) => result.signedUrl,
        );

        setEvidenceFiles((prev) => [...prev, ...newFileKeys]);
        setSignedImgUrls((prev) => [...prev, ...newSignedUrls]);

        toast.success(
          `Successfully uploaded ${successfulUploads.length} file(s)`,
        );
      }

      if (successfulUploads.length < validFiles.length) {
        setUploadErrorMessage("Some files failed to upload");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadErrorMessage(
        `File upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change - fixed to prevent duplicate calls
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    handleMultipleFilesUpload(files);

    e.target.value = "";
  };

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
    setSignedImgUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    // If admin is in Needs Attention view, only allow reassignment
    if (isAdminNeedsAttentionView) {
      if (!selectedNewStaff) {
        toast.warn("Select a staff member to reassign");
        return;
      }
      setIsSubmitting(true);
      try {
        await axiosInstance.put(
          `/api/washroom/${id}/assign`,
          { assignedStaff: selectedNewStaff },
        );

        toast.success("Staff reassigned successfully");
        navigate(`/washroom-management/washrooms`);
      } catch (error) {
        console.error("Reassignment error:", error);
        toast.error("Failed to reassign staff");
      } finally {
        setIsSubmitting(false);
      }
      return; // stop further processing
    }

    // AUTO INCIDENT CREATION LOGIC
    let incidentCreated = false;
    if (selectedStatus === "Needs Attention" && notes.trim()) {
      try {
        await createAutoIncident(status, notes);
        incidentCreated = true;
      } catch (error) {
        console.error("Auto incident creation failed:", error);
        // Continue with status update even if incident creation fails
      }
    }

    // Existing full-update flow for other users/statuses
    if (
      selectedStatus === "Clean" &&
      Object.values(checklist).some((v) => !v)
    ) {
      toast.error(
        "Please complete all checklist items before marking as clean",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // If admin (not in the special view) and selected Needs Attention with selectedNewStaff, keep existing reassignment step
      if (isAdmin && selectedStatus === "Needs Attention" && selectedNewStaff) {
        try {
          await axiosInstance.put(
            `/api/washroom/${id}/assign`,
            { assignedStaff: selectedNewStaff },
          );

          toast.success("Staff reassigned successfully");
        } catch (error) {
          console.error("Reassignment error:", error);
          toast.error("Failed to reassign staff");
          return;
        }
      }

      const payload = {
        status: selectedStatus,
        notes,
        checklist: selectedStatus === "Clean" ? checklist : null,
        resolveIssues: selectedStatus === "Clean",
        evidenceFiles: evidenceFiles,
        timeTracking: startTime
          ? {
              startTime: startTime.toISOString(),
              endTime:
                selectedStatus === "Clean" ? new Date().toISOString() : null,
              duration: elapsedTime,
            }
          : null,
        reassignedStaff: selectedNewStaff || undefined,
      };

      await axiosInstance.put(`/api/washroom/${id}/status`, payload);

      toast.success(
        incidentCreated
          ? "Status updated and incident ticket created successfully"
          : "Status updated successfully",
      );
      navigate(`/washroom-management/washrooms`);
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolveIssue = async (washroomId: string, issueId: string) => {
    try {
      await axiosInstance.put(
        `/api/washroom/${washroomId}/issues/${issueId}/resolve`,
        {},
      );

      const response = await axiosInstance.get(
        `/api/washroom/${washroomId}`,
      );
      setStatus(response.data.message);

      toast.success("Issue marked as resolved");
    } catch (error) {
      toast.error("Failed to resolve issue");
    }
  };

  // Handle status change - start timer for In-Progress
  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);

    if (newStatus === "In-Progress" && !startTime) {
      setStartTime(new Date());
      setElapsedTime("00:00:00");
    } else if (newStatus !== "In-Progress") {
      setStartTime(null);
      setElapsedTime("00:00:00");
    }
  };

  if (loading) {
    return <DotLoader />;
  }

  return (
    <div className="px-4 py-6 mt-14 min-h-screen bg-slate-50/50">
      <div className="max-w-6xl mx-auto">
        {/* Top Bar with Back Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/washroom-management/washrooms`)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors font-semibold text-sm bg-white px-4 py-2.5 rounded-xl shadow-sm border border-slate-200 cursor-pointer"
          >
            <ArrowLeft size={16} />
            <span>Back to Washrooms</span>
          </button>
        </div>

        {/* Washroom Info Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 shadow-xl relative overflow-hidden mb-6">
          {/* Absolute decorative accent circles */}
          <div className="absolute right-[-20px] top-[-20px] w-36 h-36 bg-teal-500/10 rounded-full blur-2xl"></div>
          <div className="absolute left-[-20px] bottom-[-20px] w-36 h-36 bg-blue-500/10 rounded-full blur-2xl"></div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
            <div>
              <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1">
                <Sparkles size={12} />
                <span>Washroom Details</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-white mb-2">
                {status?.washroomName || "Unknown Washroom"}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-300">
                <div className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-slate-400" />
                  <span>Floor: {status?.floor || "N/A"}</span>
                </div>
                {status?.assignedStaff && (
                  <div className="flex items-center gap-1.5">
                    <User size={14} className="text-slate-400" />
                    <span>
                      Assigned: {getUserNameById(status.assignedStaff)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Current Status Pill */}
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                  Current Status
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-sm border ${
                      status?.currentStatus === "Clean"
                        ? "bg-green-500/10 text-green-400 border-green-500/30"
                        : status?.currentStatus === "In-Progress"
                          ? "bg-yellow-50/10 text-yellow-400 border-yellow-500/30"
                          : status?.currentStatus === "Closed"
                            ? "bg-slate-500/20 text-slate-400 border-slate-500/30"
                            : "bg-red-500/10 text-red-400 border-red-500/30"
                    }`}
                  >
                    {status?.currentStatus === "Clean" && (
                      <CheckCircle2 size={13} />
                    )}
                    {status?.currentStatus === "In-Progress" && (
                      <Clock
                        size={13}
                        className="animate-spin"
                        style={{ animationDuration: "3s" }}
                      />
                    )}
                    {status?.currentStatus === "Closed" && <Lock size={13} />}
                    {(status?.currentStatus === "Needs Attention" ||
                      status?.currentStatus === "Not Cleaned") && (
                      <AlertTriangle size={13} />
                    )}
                    {status?.currentStatus || "Not Cleaned"}
                    {status?.cleaningDuration &&
                      status.currentStatus === "Clean" && (
                        <span className="text-[10px] bg-green-500/20 px-1.5 py-0.5 rounded-full ml-1">
                          {status.cleaningDuration}m
                        </span>
                      )}
                  </span>
                </div>
              </div>

              {status?.lastCleanedAt && (
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                    Last Action
                  </span>
                  <span className="text-xs text-slate-200 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700/50 flex items-center gap-1.5">
                    <Calendar size={13} className="text-teal-400" />
                    {format(
                      new Date(status.lastCleanedAt),
                      "MMM dd, yyyy HH:mm",
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Two Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Status Selection */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
              <ClipboardList className="text-teal-500" size={20} />
              <span>Update Washroom State</span>
            </h2>

            {/* Status Selection Cards Grid */}
            {(canUpdateStatus || isAdmin) && (
              <div className="mb-6">
                <label className="block text-slate-700 text-sm font-semibold mb-3">
                  {canUpdateStatus ? "Select New Status" : "Washroom Status"}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {statusOptions.map((opt) => {
                    const isSelected = selectedStatus === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleStatusChange(opt.value)}
                        disabled={isAdminNeedsAttentionView || !canUpdateStatus}
                        className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                          canUpdateStatus ? "cursor-pointer" : "cursor-default opacity-85"
                        } ${
                          isSelected
                            ? `${opt.activeBorder} ${opt.activeBg} ${opt.activeText} shadow-md scale-[1.02]`
                            : "border-slate-100 bg-white hover:border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div
                          className={`p-2 rounded-full mb-2 ${isSelected ? opt.iconActiveBg : "bg-slate-100"}`}
                        >
                          <opt.icon
                            className={`h-5 w-5 ${isSelected ? opt.activeText : "text-slate-500"}`}
                          />
                        </div>
                        <span className="text-xs font-semibold">
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedStatus === "Needs Attention" && canUpdateStatus && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-amber-600 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                    <ShieldAlert size={14} className="shrink-0" />
                    <span>
                      An incident ticket will be automatically created when you
                      submit with notes.
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Reassign Staff (Admin View or Admin in Needs Attention mode) */}
            {isAdmin && status?.currentStatus === "Needs Attention" && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <label className="block text-slate-800 text-sm font-semibold mb-2">
                  Reassign to Maintenance Staff
                </label>
                <div className="relative">
                  <select
                    value={selectedNewStaff}
                    onChange={(e) => setSelectedNewStaff(e.target.value)}
                    className="w-full border border-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-slate-50 text-slate-800 transition-all font-medium text-sm"
                  >
                    <option value="">Select staff member</option>
                    {getWashroomMaintenanceStaff().map((user: any) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}{" "}
                        {user.employeeId ? `(${user.employeeId})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {getWashroomMaintenanceStaff().length === 0 && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-3 rounded-xl border border-amber-200 mt-2">
                    No washroom maintenance staff found. Please check if role
                    configurations are set up.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Actions (Checklist, Uploads, Time, Notes) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Time Tracking Card (Active when status is In-Progress) */}
            {selectedStatus === "In-Progress" && startTime && (
              <div className="bg-gradient-to-br from-blue-600 to-indigo-850 text-white rounded-2xl p-6 shadow-lg border border-blue-700 relative overflow-hidden">
                <div className="absolute right-[-10px] top-[-10px] w-24 h-24 bg-white/5 rounded-full blur-xl"></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-200 animate-pulse" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">
                      Cleaning Task Active
                    </h3>
                    <p className="text-xs text-blue-200">
                      Timer is tracking elapsed time
                    </p>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-mono font-bold tracking-wider">
                    {elapsedTime}
                  </span>
                  <span className="text-xs text-blue-200">elapsed</span>
                </div>
                <div className="text-xs text-blue-100 flex items-center gap-1.5 mt-2 bg-blue-800/40 p-2.5 rounded-xl">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-ping"></span>
                  <span>Started at {format(startTime, "hh:mm:ss a")}</span>
                </div>
                <p className="text-[10px] text-blue-200 mt-4 leading-relaxed">
                  * Cleaning duration will be saved to log when status is saved
                  as "Clean".
                </p>
              </div>
            )}

            {/* Cleaning Checklist Card (Active when status is Clean) */}
            {selectedStatus === "Clean" && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                    <CheckSquare className="text-green-500" size={18} />
                    <span>Cleaning Checklist</span>
                  </h3>
                  <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                    {Object.values(checklist).filter(Boolean).length} of{" "}
                    {checklistItems.length}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
                  <div
                    className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${(Object.values(checklist).filter(Boolean).length / checklistItems.length) * 100}%`,
                    }}
                  ></div>
                </div>

                <div className="space-y-2.5">
                  {checklistItems.map((item) => {
                    const isChecked = checklist[item];
                    return (
                      <label
                        key={item}
                        htmlFor={`check-${item}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border select-none transition-all duration-150 ${
                          canUpdateStatus ? "cursor-pointer" : "cursor-default"
                        } ${
                          isChecked
                            ? "border-green-200 bg-green-50/20 text-slate-700 font-semibold"
                            : "border-slate-100 hover:border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`check-${item}`}
                          checked={isChecked}
                          onChange={(e) => {
                            if (!canUpdateStatus) return;
                            setChecklist({
                              ...checklist,
                              [item]: e.target.checked,
                            });
                          }}
                          disabled={!canUpdateStatus}
                          className="hidden"
                        />
                        <div
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                            isChecked
                              ? "bg-green-500 border-green-500 text-white"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {isChecked && <Check size={12} strokeWidth={3} />}
                        </div>
                        <span
                          className={`text-sm ${isChecked ? "line-through text-slate-400" : ""}`}
                        >
                          {item}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes Card (Active when status is Needs Attention) */}
            {selectedStatus === "Needs Attention" && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-2">
                  <FileText className="text-amber-500" size={18} />
                  <span>Issue Notes *</span>
                </h3>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-slate-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 text-slate-800 placeholder-slate-400 transition-all text-sm"
                  rows={4}
                  placeholder="Describe the issue in detail... (e.g. Broken faucet, blocked toilet)"
                  readOnly={isAdminNeedsAttentionView || !canUpdateStatus}
                  required
                />
                <p className="text-[11px] text-slate-400 mt-2">
                  Detailed notes are required to create an incident ticket
                  automatically.
                </p>
              </div>
            )}

            {/* Evidence Upload Card */}
            {(selectedStatus === "Clean" ||
              selectedStatus === "Needs Attention") && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                  <h3 className="font-bold text-slate-900 text-base mb-4 flex items-center gap-2">
                    <Camera className="text-teal-500" size={18} />
                    <span>Evidence Photos</span>
                  </h3>

                  {canUpdateStatus && !isAdminNeedsAttentionView ? (
                    <div className="relative">
                      <label
                        htmlFor="evidence-upload"
                        className={`flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-slate-100/70 border-2 border-dashed ${
                          uploadErrorMessage
                            ? "border-red-300"
                            : "border-slate-300 hover:border-teal-500/50"
                        } p-6 rounded-2xl transition-all duration-200 group`}
                      >
                        <input
                          id="evidence-upload"
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleFileInputChange}
                          disabled={uploading}
                          className="hidden"
                        />
                        <Upload
                          className={`h-8 w-8 ${uploadErrorMessage ? "text-red-400" : "text-slate-400 group-hover:text-teal-500"} mb-2.5 transition-colors`}
                        />
                        <div className="text-center">
                          <span className="text-sm font-semibold text-slate-700 block mb-0.5">
                            {uploading
                              ? "Uploading files..."
                              : "Upload Evidence"}
                          </span>
                          <span className="text-xs text-slate-400 block">
                            Drag files or click to browse (Max 5MB)
                          </span>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-500 border border-slate-100">
                      Evidence uploads are read-only for administrators.
                    </div>
                  )}

                  {/* Upload Error Message */}
                  {uploadErrorMessage && (
                    <div className="mt-3 text-xs text-red-600 bg-red-50 p-2.5 rounded-xl border border-red-200">
                      {uploadErrorMessage}
                    </div>
                  )}

                  {/* Uploading progress indicator */}
                  {uploading && (
                    <div className="mt-3 p-3 bg-teal-50/50 rounded-xl border border-teal-100">
                      <div className="flex justify-between text-xs font-semibold text-teal-700 mb-1.5">
                        <span>Uploading files...</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-teal-500 h-1.5 rounded-full animate-pulse w-2/3"></div>
                      </div>
                    </div>
                  )}

                  {/* Evidence Gallery */}
                  {signedImgUrls.length > 0 && (
                    <div className="mt-5">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-3">
                        Attached Evidence ({signedImgUrls.length})
                      </span>
                      <div className="grid grid-cols-3 gap-3">
                        {signedImgUrls.map((url, index) => (
                          <div
                            key={`${url}-${index}`}
                            className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shadow-sm"
                          >
                            <img
                              src={url}
                              alt={`Evidence ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src =
                                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='12' fill='%239ca3af'%3EImage%3C/text%3E%3C/svg%3E";
                              }}
                            />
                            {canUpdateStatus && !isAdminNeedsAttentionView && (
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(index)}
                                className="absolute top-1.5 right-1.5 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-md transform hover:scale-105"
                                title="Remove file"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Action Buttons Panel */}
            {canUpdateStatus || isAdminNeedsAttentionView ? (
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(`/washroom-management/washrooms`)}
                  className="flex-1 bg-white border border-slate-200 text-slate-700 py-3 px-4 rounded-xl font-semibold hover:bg-slate-50 transition-colors shadow-sm text-sm cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStatusUpdate}
                  disabled={
                    isSubmitting ||
                    !selectedStatus ||
                    uploading ||
                    (isAdminNeedsAttentionView && !selectedNewStaff)
                  }
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-3 px-4 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none shadow-md shadow-teal-500/10 text-sm flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <span>
                      {isAdminNeedsAttentionView
                        ? "Reassign Staff"
                        : "Update Status"}
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(`/washroom-management/washrooms`)}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3.5 px-4 rounded-xl font-semibold transition-colors shadow-sm text-sm text-center cursor-pointer flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={16} />
                  <span>Go Back</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reported Issues Section */}
        {status && (status.issues ?? []).length > 0 && (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 animate-fadeIn">
            <h3 className="text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
              <ShieldAlert className="text-red-500" size={20} />
              <span>Active Reported Issues</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(status.issues ?? []).map((issue) => (
                <div
                  key={issue._id}
                  className={`border rounded-xl p-4 transition-all ${
                    issue.resolved
                      ? "border-green-100 bg-green-50/10"
                      : "border-red-100 bg-red-50/10"
                  }`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-1">
                      <p
                        className={`font-semibold text-sm ${issue.resolved ? "text-slate-500 line-through" : "text-slate-800"}`}
                      >
                        {issue.notes}
                      </p>
                      <div className="text-xs text-slate-400 space-y-0.5">
                        <p>
                          Reported:{" "}
                          {format(
                            new Date(issue.reportedAt),
                            "MMM dd, yyyy HH:mm",
                          )}
                          {issue.reportedBy &&
                            ` by ${getUserNameById(issue.reportedBy)}`}
                        </p>
                        {issue.resolvedAt && (
                          <p className="text-green-600 font-medium">
                            Resolved:{" "}
                            {format(
                              new Date(issue.resolvedAt),
                              "MMM dd, yyyy HH:mm",
                            )}
                            {issue.resolvedBy &&
                              ` by ${getUserNameById(issue.resolvedBy)}`}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${
                          issue.resolved
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-red-100 text-red-700 border border-red-200"
                        }`}
                      >
                        {issue.resolved ? "Resolved" : "Pending"}
                      </span>

                      {!issue.resolved &&
                        canResolveIssues &&
                        !isAdminNeedsAttentionView && (
                          <button
                            onClick={() =>
                              issue._id &&
                              handleResolveIssue(id || "", issue._id)
                            }
                            className="text-[11px] font-bold bg-slate-900 hover:bg-slate-800 text-white px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                          >
                            Resolve
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WashroomStatus;
